"use client";

import { FormEvent, useCallback, useEffect, useState } from "react";

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
    if (success) {
      setMessage("");
      setEmail("");
      setSuccess(false);
    }
  }, [success]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message.trim(),
          email: email.trim() || undefined
        })
      });

      const body = (await response.json()) as { error?: string; details?: string };

      if (!response.ok) {
        throw new Error(body.details ?? body.error ?? "Failed to submit");
      }

      setSuccess(true);
      setTimeout(close, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="feedback-button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? "Close feedback" : "Leave feedback"}
      >
        <span className="feedback-button-tooltip">
          {open ? "Click to close" : "Click here to leave feedback"}
        </span>
        <span className="feedback-button-icon">
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
      </button>

      {open && (
        <div
          className="feedback-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="feedback-modal-title"
        >
          <div className="feedback-panel-window">
            <div className="feedback-modal-header">
              <h2 id="feedback-modal-title" className="feedback-modal-title">
                Leave feedback
              </h2>
              <button
                type="button"
                className="feedback-modal-close"
                onClick={close}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {success ? (
              <div className="feedback-modal-body">
                <p className="feedback-success">Thank you! Your feedback has been submitted.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="feedback-modal-form">
                <div className="feedback-modal-body">
                  <label className="feedback-field">
                    <span className="feedback-label">Message</span>
                    <textarea
                      className="form-input feedback-textarea"
                      placeholder="What would you like to share?"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={4}
                      maxLength={2000}
                    />
                    <span className="feedback-char-count">{message.length}/2000</span>
                  </label>
                  <label className="feedback-field">
                    <span className="feedback-label">Email (optional)</span>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </label>
                  {error && <p className="feedback-error">{error}</p>}
                </div>
                <div className="feedback-modal-actions">
                  <button type="button" className="btn btn--ghost" onClick={close}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn--primary" disabled={submitting}>
                    {submitting ? "Sending…" : "Submit"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
